import {
  Component,
  OnInit,
  OnDestroy,
  Optional
} from '@angular/core';
import { take, takeWhile } from 'rxjs/operators';
import { ChatFacade } from '../../../+store/chat/chat.facade';
import { ICustomerUser } from '@care-portals/api-interfaces';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';

@Component({
  selector: 'appgen-chat-main',
  styleUrls: ['./chat-main.component.less'],
  templateUrl: './chat-main.component.html'
})
export class ChatMainComponent implements OnInit, OnDestroy {
  isConnected = false;
  newMsg = '';

  activeView = 'inbox';
  isActive = false;
  customer: ICustomerUser;
  /** True after the user (or mask) has begun tearing this instance down. */
  private isClosing = false;
  /**
   * The chat session this instance was mounted for. Destroy-time teardown is
   * tagged with it so a superseded instance — its drawer still animating out
   * after a newer chat has opened — cannot clear the live session out from
   * under it.
   */
  private sessionId: number = null;
  /**
   * The session currently in the store. The sidebar host is not re-created
   * between opens (openChat keeps isSidebarOpen true), so that instance
   * outlives the session it mounted for and its close must act on the session
   * actually on screen. A drawer instance is per-open and can be superseded
   * while still on screen, so it stays scoped to its own session instead.
   */
  private liveSessionId: number = null;

  constructor(
    private readonly chatFacade: ChatFacade,
    @Optional() private readonly nzDrawerRef: NzDrawerRef
  ) {}

  async ngOnInit() {
    this.isActive = true;

    // Emits synchronously — openChat has already landed in the store by the
    // time the drawer/sidebar host mounts us.
    this.chatFacade.sessionId$
      .pipe(take(1))
      .subscribe(sessionId => (this.sessionId = sessionId));

    this.chatFacade.sessionId$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(sessionId => {
        this.liveSessionId = sessionId;
        // A drawer instance is replaced per open, so a session change means it
        // has been superseded and must stay tagged with its own session. The
        // sidebar host is reused across opens instead: it is serving the new
        // session now, so it adopts it and un-latches a close that the reducer
        // discarded, which is what left the X doing nothing.
        if (!this.nzDrawerRef && sessionId !== this.sessionId) {
          this.sessionId = sessionId;
          this.isClosing = false;
        }
      });

    this.chatFacade.activeCustomer$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(customer => {
        // Keep local customer in sync — including clears — so the template
        // never renders stale identity after closeChat races a remount.
        // Exception: once we're closing, closeChat() has already nulled
        // activeCustomer while the drawer is still animating out. Holding the
        // last customer keeps the body rendered instead of flashing the
        // empty-state template through the slide-out.
        if (this.isClosing) return;
        this.customer = customer ?? null;
      });

    this.chatFacade.mode$.pipe(takeWhile(() => this.isActive)).subscribe(mode => {
      if (mode) {
        this.activeView = mode;
      }
    });
  }

  ngOnDestroy() {
    this.isActive = false;
    // Sidebar host is destroyed when openChat(..., 'drawer') flips
    // isSidebarOpen off. Calling closeChat() here would wipe activeCustomer
    // before the drawer ChatMain mounts — drawer appears empty/broken and
    // Angular may error on customer.firstName, which then blocks later opens.
    // Only the drawer instance should clear store state on destroy (mask close
    // does not go through closeDrawer()).
    if (this.nzDrawerRef && !this.isClosing) {
      this.chatFacade.removeParticipant(this.sessionId);
      this.chatFacade.closeChat(this.sessionId);
    }
  }

  closeDrawer() {
    this.isClosing = true;
    // The sidebar host outlives the session it mounted for, so its close acts
    // on whatever session is live. A drawer stays scoped to its own: a chat
    // opened over it (session overlay, sidebar) supersedes it while its X is
    // still clickable, and that click must not tear down the newer chat.
    const sessionId = this.nzDrawerRef
      ? this.sessionId
      : this.liveSessionId ?? this.sessionId;
    this.chatFacade.removeParticipant(sessionId);
    this.chatFacade.closeChat(sessionId);
    if (this.nzDrawerRef) {
      this.nzDrawerRef.close();
    }
  }

  ping() {
    if (!this.customer?._id) return;
    this.chatFacade.sendPing(this.customer._id);
  }

  forwardCS() {
    if (!this.customer?._id) return;
    this.chatFacade.forwardCS(this.customer._id);
  }

  forwardPH() {
    if (!this.customer?._id) return;
    this.chatFacade.forwardPH(this.customer._id);
  }

  joinVideo() {
    if (!this.customer?._id) return;
    this.chatFacade.joinVideo(this.customer._id);
  }
}
